/**
 * Reference: https://github.com/stacktracejs/error-stack-parser
 *
 * LICENSE
 * Copyright (c) 2017 Eric Wendelin and other contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
 * of the Software, and to permit persons to whom the Software is furnished to do
 * so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { IPubStackFrame } from '../types';
import StackFrame from './stackframe';

const CHROME_IE_STACK_REGEXP = /^\s*at .*(\S+\:\d+|\(native\))/m;
const SAFARI_NATIVE_CODE_REGEXP = /^(eval@)?(\[native code\])?$/;

/**
 * Given an Error object, extract the most information from it.
 *
 * @param {Error} error object
 * @return {Array} of StackFrames
 */
export function parse (error: Error): IPubStackFrame[] {
  if (error.stack && error.stack.match(CHROME_IE_STACK_REGEXP)) {
    return parseV8OrIE(error);
  } else if (error.stack) {
    return parseFFOrSafari(error);
  } else {
    throw new Error('Cannot parse given Error object');
  }
}

function getParameterByName(urlLike: string, name: string) {
  urlLike = urlLike.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
  const results = regex.exec(urlLike);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

function extractAppInfo(urlLike: string) {
  // pn: pub app name
  // ps: gitlab commit id substring
  const keys = ['pn', 'ps'];
  const pubAppName = getParameterByName(urlLike, keys[0]);
  let resourceId = getParameterByName(urlLike, keys[1]);
  if (resourceId) {
    if (resourceId.indexOf(':') !== -1) {
      resourceId = resourceId.split(':')[0];
    }
    if (resourceId.indexOf(' ') !== -1) {
      resourceId = resourceId.split(' ')[0];
    }
  }

  return {
    pubAppName,
    resourceId,
  };
}

// Separate line and column numbers from a string of the form: (URI:Line:Column)
function extractLocation (urlLike: string) {
  // Fail-fast but return locations like "(native)"
  if (urlLike.indexOf(':') === -1) {
    return [urlLike];
  }

  const regExp = /(.+?)(?:\:(\d+))?(?:\:(\d+))?$/;
  const parts: any = regExp.exec(urlLike.replace(/[\(\)]/g, ''));
  return [parts[1], parts[2], parts[3]];
}

function parseV8OrIE (error: any) {
  const filtered = error.stack.split('\n').filter(
    (line: string) => !!line.match(CHROME_IE_STACK_REGEXP));

  return filtered.map((line: string) => {
    const { pubAppName, resourceId } = extractAppInfo(line);
    if (line.indexOf('(eval ') > -1) {
      // Throw away eval information until we implement stacktrace.js/stackframe#8
      line = line.replace(/eval code/g, 'eval').replace(/(\(eval at [^\()]*)|(\)\,.*$)/g, '');
    }
    const tokens = line.replace(/^\s+/, '').replace(/\(eval code/g, '(').split(/\s+/).slice(1);
    const locationParts = extractLocation(tokens.pop()!);
    const functionName = tokens.join(' ') || undefined;
    const fileName = ['eval', '<anonymous>'].indexOf(locationParts[0]) > -1 ? undefined : locationParts[0];

    return {
      stackFrame: new StackFrame({
        functionName,
        fileName,
        lineNumber: locationParts[1],
        columnNumber: locationParts[2],
        source: line,
      }),
      pubAppName,
      resourceId,
    };
  });
}

function parseFFOrSafari(error: any) {
  const filtered = error.stack.split('\n').filter(
    (line: string) => !line.match(SAFARI_NATIVE_CODE_REGEXP));

  return filtered.map((line: string) => {
    const { pubAppName, resourceId } = extractAppInfo(line);
    // Throw away eval information until we implement stacktrace.js/stackframe#8
    if (line.indexOf(' > eval') > -1) {
      line = line.replace(/ line (\d+)(?: > eval line \d+)* > eval\:\d+\:\d+/g, ':$1');
    }

    if (line.indexOf('@') === -1 && line.indexOf(':') === -1) {
      // Safari eval frames only have function names and nothing else
      return {
        stackFrame: new StackFrame({
          functionName: line,
        }),
        pubAppName,
        resourceId,
      };
    } else {
      const functionNameRegex = /((.*".+"[^@]*)?[^@]*)(?:@)/;
      const matches = line.match(functionNameRegex);
      const functionName = matches && matches[1] ? matches[1] : undefined;
      const locationParts = extractLocation(line.replace(functionNameRegex, ''));

      return {
        stackFrame: new StackFrame({
          functionName,
          fileName: locationParts[0],
          lineNumber: locationParts[1],
          columnNumber: locationParts[2],
          source: line,
        }),
        pubAppName,
        resourceId,
      };
    }
  });
}
